import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getCustomer } from '../services/customer_service'
import './CustomerDetails.css'

const getContactName = (customer) =>
  customer.contact_name ||
  customer.cust_contact_name ||
  customer.contactName ||
  '-'
const getSalesExec = (customer) =>
  customer.sales_exec ||
  customer.sales_exec_name ||
  customer.salesExecutive ||
  customer.sales_executive ||
  '-'
const getCustomerType = (customer) =>
  customer.type ||
  customer.customertype ||
  customer.customer_type ||
  customer.cust_type ||
  customer.customerType ||
  '-'
const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getNumber = (record, keys, fallback = 0) => {
  const value = getValue(record, keys, null)
  if (value === null) return fallback
  const number = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(number) ? number : fallback
}
const getOrderId = (order) =>
  getValue(order, ['orderid', 'orderId', 'order_id', 'id'], '—')
const getOrderDate = (order) =>
  getValue(order, [
    'date',
    'order_date',
    'orderDate',
    'request_date',
    'created_at',
    'createdAt',
  ])
const getPaymentDate = (payment) =>
  getValue(payment, [
    'date_of_payment',
    'payment_date',
    'paymentDate',
    'date',
    'created_at',
  ])
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(value || 0)

function getRecords(customer, key) {
  const aliases = {
    orders: ['orders', 'orderList', 'customerOrders'],
    payments: ['payments', 'paymentList', 'customerPayments'],
  }
  const records = (aliases[key] || [key])
    .map((candidate) => customer?.[candidate] || customer?.data?.[candidate])
    .find(Array.isArray)
  return Array.isArray(records) ? records : []
}

function SummaryItem({ label, value, emphasis = false }) {
  return (
    <div
      className={`customer-summary__item${emphasis ? ' customer-summary__item--emphasis' : ''}`}
    >
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function CustomerDetails() {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadCustomer = async () => {
      setIsLoading(true)
      setError('')
      try {
        setCustomer(await getCustomer(customerId))
      } catch (requestError) {
        setError(requestError.message)
      } finally {
        setIsLoading(false)
      }
    }

    loadCustomer()
  }, [customerId])

  return (
    <div className='customer-details-page'>
      <Link className='customer-details-page__back' to='/customers'>
        Back to customers
      </Link>
      {isLoading ? (
        <p className='customer-details-page__status'>Loading customer...</p>
      ) : error ? (
        <div className='customer-details-page__error' role='alert'>
          {error}
        </div>
      ) : customer ? (
        <>
          <header className='customer-details-page__header'>
            <div>
              <span className='app-content__eyebrow'>Customer account</span>
              <h1>
                {customer.name ||
                  customer.cust_name ||
                  customer.company ||
                  'Customer'}
              </h1>
              <p>
                Review account details, order activity, and payment history.
              </p>
            </div>
            <span className='customer-details-page__status-badge'>
              Active account
            </span>
          </header>
          <section
            className='customer-details-card customer-details-card--info'
            aria-labelledby='customer-info-title'
          >
            <div className='customer-section-heading'>
              <div>
                <span className='app-content__eyebrow'>Profile</span>
                <h2 id='customer-info-title'>Customer information</h2>
              </div>
              <span className='customer-section-heading__mark'>01</span>
            </div>
            <div className='customer-info-grid'>
              <div>
                <span>Name</span>
                <strong>
                  {customer.name ||
                    customer.cust_name ||
                    customer.company ||
                    '-'}
                </strong>
              </div>
              <div>
                <span>Type</span>
                <strong>{getCustomerType(customer)}</strong>
              </div>
              <div>
                <span>Contact name</span>
                <strong>{getContactName(customer)}</strong>
              </div>
              <div>
                <span>Contact</span>
                <strong>
                  {customer.contact ||
                    customer.cust_contact ||
                    customer.phone ||
                    '-'}
                </strong>
              </div>
              <div>
                <span>Region</span>
                <strong>
                  {customer.region || customer.region_name || '-'}
                </strong>
              </div>
              <div>
                <span>Sales executive</span>
                <strong>{getSalesExec(customer)}</strong>
              </div>
              <div className='customer-info-grid__wide'>
                <span>Address</span>
                <strong>
                  {customer.address || customer.cust_address || '-'}
                </strong>
              </div>
            </div>
          </section>
          <CustomerActivity customer={customer} />
        </>
      ) : (
        <p className='customer-details-page__status'>Customer not found.</p>
      )}
    </div>
  )
}

function CustomerActivity({ customer }) {
  const orders = getRecords(customer, 'orders')
  const payments = getRecords(customer, 'payments')
  const calculatedTotals = orders.reduce(
    (summary, order) => {
      summary.gross += getNumber(order, [
        'gross_amount',
        'grossAmount',
        'gross',
        'grosstotal',
      ])
      summary.discount += getNumber(order, [
        'discount_amount',
        'discountAmount',
        'discount',
      ])
      summary.net += getNumber(order, [
        'net_amount',
        'netAmount',
        'net',
        'total',
        'nettotal',
      ])
      return summary
    },
    { gross: 0, discount: 0, net: 0 },
  )
  const calculatedPaid = payments.reduce(
    (total, payment) =>
      total +
      getNumber(payment, [
        'amount',
        'paid_amount',
        'paidAmount',
        'total_amount',
      ]),
    0,
  )
  const orderSummary = customer.orderSummary || {}
  const summary = orderSummary
  const totalOrders = getNumber(
    orderSummary,
    ['totalOrders', 'total_orders'],
    getNumber(summary, ['totalOrders', 'total_orders'], orders.length),
  )
  const totals = {
    gross: getNumber(
      orderSummary,
      ['totalGross', 'total_gross'],
      getNumber(
        summary,
        ['totalGross', 'total_gross', 'grossAmount', 'gross_amount'],
        calculatedTotals.gross,
      ),
    ),
    discount: getNumber(
      orderSummary,
      ['totalDiscount', 'total_discount'],
      getNumber(
        summary,
        [
          'totalDiscount',
          'total_discount',
          'discountAmount',
          'discount_amount',
        ],
        calculatedTotals.discount,
      ),
    ),
    net: getNumber(
      orderSummary,
      ['totalNet', 'total_net'],
      getNumber(
        summary,
        ['totalNet', 'total_net', 'netAmount', 'net_amount'],
        calculatedTotals.net,
      ),
    ),
  }
  const paid = getNumber(
    orderSummary,
    ['totalPaid', 'total_paid'],
    getNumber(
      summary,
      ['totalPaid', 'total_paid', 'paidAmount', 'paid_amount'],
      calculatedPaid,
    ),
  )
  const owed = getNumber(
    orderSummary,
    ['totalBalance', 'total_balance'],
    getNumber(
      summary,
      ['totalBalance', 'total_balance', 'amountOwed', 'amount_owed'],
      totals.net - paid,
    ),
  )

  return (
    <>
      <section
        className='customer-details-card customer-details-card--summary'
        aria-labelledby='orders-summary-title'
      >
        <div className='customer-section-heading'>
          <div>
            <span className='app-content__eyebrow'>Financial snapshot</span>
            <h2 id='orders-summary-title'>Orders summary</h2>
          </div>
          <span className='customer-section-heading__mark'>02</span>
        </div>
        <div className='customer-summary-grid'>
          <SummaryItem
            label='Total orders'
            value={
              customer.orderSummary?.totalOrders ||
              customer.orderSummary?.total_orders ||
              orders.length
            }
          />
          <SummaryItem
            label='Gross amount'
            value={formatCurrency(totals.gross)}
          />
          <SummaryItem
            label='Discount amount'
            value={formatCurrency(totals.discount)}
          />
          <SummaryItem
            label='Net amount'
            value={formatCurrency(totals.net)}
            emphasis
          />
          <SummaryItem label='Paid amount' value={formatCurrency(paid)} />
          <SummaryItem
            label='Amount owed'
            value={formatCurrency(owed)}
            emphasis
          />
        </div>
      </section>
      <div className='customer-activity-grid'>
        <ActivityTable
          title='Orders'
          eyebrow='Order activity'
          number='03'
          emptyMessage='No orders recorded for this customer yet.'
          hasData={orders.length > 0}
          className='customer-activity-card--orders'
        >
          <table className='customer-activity-table'>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Gross amount</th>
                <th>Discount amount</th>
                <th>Net amount</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => {
                const gross = getNumber(order, [
                  'gross_amount',
                  'grossAmount',
                  'gross',
                  'grosstotal',
                ])
                const discount = getNumber(order, [
                  'discount_amount',
                  'discountAmount',
                  'discount',
                ])
                const net = getNumber(order, [
                  'net_amount',
                  'netAmount',
                  'net',
                  'total',
                  'nettotal',
                ])
                const orderId = getOrderId(order)
                return (
                  <tr key={`${orderId}-${index}`}>
                    <td>
                      <Link to={`/sales/orders/${orderId}`}>{orderId}</Link>
                    </td>
                    <td>{formatDate(getOrderDate(order))}</td>
                    <td>{formatCurrency(gross)}</td>
                    <td>{formatCurrency(discount)}</td>
                    <td className='customer-activity-table__strong'>
                      {formatCurrency(net)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </ActivityTable>
        <ActivityTable
          title='Payments'
          eyebrow='Payment activity'
          number='04'
          emptyMessage='No payments recorded for this customer yet.'
          hasData={payments.length > 0}
          className='customer-activity-card--payments'
        >
          <table className='customer-activity-table'>
            <thead>
              <tr>
                <th>Payment mode</th>
                <th>Date of payment</th>
                <th>Amount</th>
                <th>Received by</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={`${getPaymentDate(payment)}-${index}`}>
                  <td>
                    {getValue(payment, [
                      'payment_mode',
                      'paymentMode',
                      'mode',
                      'method',
                    ])}
                  </td>
                  <td>{formatDate(getPaymentDate(payment))}</td>
                  <td className='customer-activity-table__strong'>
                    {formatCurrency(
                      getNumber(payment, [
                        'amount',
                        'paid_amount',
                        'paidAmount',
                        'total_amount',
                      ]),
                    )}
                  </td>
                  <td>
                    {getValue(payment, [
                      'received_by',
                      'receivedBy',
                      'receiver',
                      'user_name',
                      'received_by_user',
                    ])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ActivityTable>
      </div>
    </>
  )
}

function ActivityTable({
  title,
  eyebrow,
  number,
  emptyMessage,
  hasData,
  className,
  children,
}) {
  return (
    <section
      className={`customer-details-card customer-activity-card ${className}`}
      aria-labelledby={`${title.toLowerCase()}-activity-title`}
    >
      <div className='customer-section-heading'>
        <div>
          <span className='app-content__eyebrow'>{eyebrow}</span>
          <h2 id={`${title.toLowerCase()}-activity-title`}>{title}</h2>
        </div>
        <span className='customer-section-heading__mark'>{number}</span>
      </div>
      {hasData ? (
        <div className='customer-table-wrap'>{children}</div>
      ) : (
        <p className='customer-activity-card__empty'>{emptyMessage}</p>
      )}
    </section>
  )
}

export default CustomerDetails
